import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { globalKeys } from 'assets/data/global-keys';
import { firebaseConfig } from 'assets/data/config';
import { useAuthStore } from '~/stores/auth.store';
import { useServices } from '#imports';
import useMessageHandler from '~/composables/useMessageHandler';
import { getUserDeviceInfo } from '~/utilities/device-info';

let swMessageListenerBound = false;
let registrationInFlight = false;

const handlePushNotification = (payload: {
  data: {
    action: string;
    actionValue: string;
  };
}) => {
  const { action, actionValue } = payload.data ?? {};
  if (action && actionValue) {
    useMessageHandler({ action, actionValue });
  }
};

export default defineNuxtPlugin({
  name: 'push-notifications-sw',
  dependsOn: ['init-client', 'firebase'],
  parallel: true,
  setup: async () => {
    if (!import.meta.client) return;
    if (!('serviceWorker' in navigator)) return;

    const requestInfo = getUserDeviceInfo();

    const { $firebaseApp } = useNuxtApp();

    const runtimeConfig = useRuntimeConfig();
    const auth = useAuthStore();
    const { $storageService, $marketingService } = useServices();

    if (!requestInfo) {
      const deviceInfo = await $marketingService.fetchDeviceInfo();
      $storageService.setSessionStorage({
        key: globalKeys.siteConfigKeys.deviceInfo,
        data: deviceInfo,
      });
    }

    const isPushNotificationsSupported = await isSupported();

    const registerAndSubscribe = async () => {
      if (registrationInFlight || !isPushNotificationsSupported) return;

      const accessToken = $storageService.getCookie<string>({
        key: globalKeys.authKeys.accessToken,
      });

      if (!accessToken) return;
      registrationInFlight = true;

      try {
        const messaging = getMessaging($firebaseApp);

        const serviceWorkerRegistration =
          await navigator.serviceWorker.register(
            `${globalKeys.pushNotificationKeys.firebaseSwScript}?config=${encodeURIComponent(JSON.stringify(firebaseConfig))}&firebaseVersion=${encodeURIComponent(`${runtimeConfig.public.firebaseVersion}`)}`,
          );

        await navigator.serviceWorker.ready;

        const currentToken = await getToken(messaging, {
          vapidKey: globalKeys.pushNotificationKeys.vapidKey,
          serviceWorkerRegistration,
        });

        if (!currentToken) {
          console.warn('[push-notifications-sw] no FCM token returned');
          return;
        }

        if (!swMessageListenerBound) {
          navigator.serviceWorker.addEventListener('message', (event) =>
            handlePushNotification(event),
          );
          navigator.serviceWorker.addEventListener(
            'backgroundmessage',
            (event) => handlePushNotification(event),
          );
          swMessageListenerBound = true;
        }

        const savedPushToken = $storageService.getSessionStorage<string>({
          key: globalKeys.pushNotificationKeys.pushTokenKey,
        });
        const savedAccessToken = $storageService.getSessionStorage<string>({
          key: globalKeys.pushNotificationKeys.pushAccessTokenKey,
        });

        if (
          savedPushToken !== currentToken ||
          savedAccessToken !== accessToken
        ) {
          await $marketingService.subscribeToTopic(currentToken);
          await $marketingService.registerPushToken(currentToken);
        }

        $storageService.setSessionStorage({
          key: globalKeys.pushNotificationKeys.pushTokenKey,
          data: currentToken,
        });
        $storageService.setSessionStorage({
          key: globalKeys.pushNotificationKeys.pushAccessTokenKey,
          data: accessToken,
        });
      } catch (error) {
        console.error('[push-notifications-sw] failed', error);
      } finally {
        registrationInFlight = false;
      }
    };

    const stop = watch(
      () => auth.isLoggedIn,
      (isLoggedIn) => {
        if (!isLoggedIn) return;
        void registerAndSubscribe();
        stop();
      },
      { immediate: true },
    );
  },
});
